import { useState, useCallback, useEffect } from 'react'
import Joyride, { Step, CallBackProps,  EVENTS } from 'react-joyride'
import { useAdminStore } from '@/store/adminStore'
import { useTranslation } from 'react-i18next'

// Custom content wrapper for tour steps
const TourContent = ({ title, content }: { title: string; content: string }) => (
  <div className="tour-content flex items-start gap-4 p-2">
    <div className="flex-1">
      <h2 className="text-lg font-semibold mb-2 text-[#525FE1]">{title}</h2>
      <p className="text-gray-600 text-sm leading-relaxed">{content}</p>
    </div>
  </div>
)

export const StudentManagementTour = () => {
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const { t } = useTranslation('tour')

  const {
    studentsTour,
    completeStudentsTour,
    setStudentsStep,
  } = useAdminStore()

  // Start tour when studentsTour.isActive changes
  useEffect(() => {
    if (studentsTour.isActive && !isRunning) {
      setIsRunning(true)
      setRun(true)
      setStepIndex(0)
    }
  }, [studentsTour.isActive, isRunning])

  // Define tour steps
  const TOUR_STEPS: Step[] = [
    {
      target: '[data-tour="student-header"]',
      content: <TourContent 
        title={t('admin.studentManagementTour.steps.header.title')}
        content={t('admin.studentManagementTour.steps.header.content')}
      />,
      disableBeacon: true,
      placement: 'center',
      spotlightPadding: 10,
      styles: {
        options: {
          zIndex: 10000
        }
      }
    },
    {
      target: '[data-tour="add-student-button"]',
      content: <TourContent 
        title={t('admin.studentManagementTour.steps.addStudent.title')}
        content={t('admin.studentManagementTour.steps.addStudent.content')}
      />,
      disableBeacon: true,
      placement: 'bottom',
      spotlightPadding: 5
    },
    {
      target: '[data-tour="download-payments"]',
      content: <TourContent 
        title={t('admin.studentManagementTour.steps.downloadPayments.title')}
        content={t('admin.studentManagementTour.steps.downloadPayments.content')}
      />,
      disableBeacon: true,
      placement: 'bottom',
      spotlightPadding: 5
    },
    {
      target: '[data-tour="student-search"]',
      content: <TourContent 
        title={t('admin.studentManagementTour.steps.search.title')}
        content={t('admin.studentManagementTour.steps.search.content')}
      />,
      disableBeacon: true,
      placement: 'bottom',
      spotlightPadding: 5
    },
    {
      target: '[data-tour="student-filters"]',
      content: <TourContent 
        title={t('admin.studentManagementTour.steps.filters.title')}
        content={t('admin.studentManagementTour.steps.filters.content')}
      />,
      disableBeacon: true,
      placement: 'left',
      spotlightPadding: 5
    }
  ]

  // Handle tour callbacks
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { action, index, status, type } = data
// console.logTour callback:', { action, index, status, type })

    // Handle tour completion
    if (status === 'finished' || status === 'skipped') {
      setRun(false)
      setIsRunning(false)
      completeStudentsTour()
      return
    }

    // Handle step changes
    if (type === EVENTS.STEP_AFTER) {
      if (action === 'next') {
        const nextStepIndex = index + 1
        if (nextStepIndex < TOUR_STEPS.length) {
          setStepIndex(nextStepIndex)
          setStudentsStep(nextStepIndex)
        } else {
          setRun(false)
          setIsRunning(false)
          completeStudentsTour()
        }
      } else if (action === 'prev') {
        const prevStepIndex = index - 1
        if (prevStepIndex >= 0) {
          setStepIndex(prevStepIndex)
          setStudentsStep(prevStepIndex)
        }
      }
    }
  }, [completeStudentsTour, setStudentsStep, TOUR_STEPS.length])

  return (
    <>
      <Joyride
        steps={TOUR_STEPS}
        run={run}
        continuous
        hideCloseButton
        showProgress
        showSkipButton
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        disableOverlayClose={true}
        // disableOverlayClose={false}
        // disableCloseOnEsc={false}
        scrollToFirstStep={true}
        scrollOffset={20}
        styles={{
          options: {
            primaryColor: '#ffffff',
            zIndex: 10000,
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
          },
          buttonNext: {
            backgroundColor: '#ffffff',
            color: '#525FE1',
            fontSize: '16px',
            padding: '10px 20px',
            borderRadius: '6px',
          },
          buttonBack: {
            color: '#666',
            fontSize: '16px',
            padding: '10px',
            marginRight: '12px',
          },
          buttonSkip: {
            color: '#666',
            fontSize: '16px',
            padding: '10px',
          },
          tooltip: {
            padding: '24px',
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            color: '#333',
            fontSize: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            maxWidth: '500px',
            minWidth: '300px',
            pointerEvents: 'auto' as const,
            transition: 'opacity 0.2s ease',
          },
          tooltipContainer: {
            textAlign: 'left' as const,
          },
          spotlight: {
            backgroundColor: 'transparent', // Completely transparent
            border: '2px solid white', // White border
            borderRadius: '8px',
            boxShadow: 'none', // Remove box shadow since we handle it in CSS
          },
        }}
        locale={{
          back: t('admin.studentManagementTour.locale.back'),
          close: t('admin.studentManagementTour.locale.close'),
          last: t('admin.studentManagementTour.locale.last'),
          next: t('admin.studentManagementTour.locale.next'),
          skip: t('admin.studentManagementTour.locale.skip'),
        }}
      />
      
      {/* Dark overlay for consistent darkening */}
      {isRunning && stepIndex === 0 && (
        <div 
          className="fixed inset-0 bg-black/70 z-[9999] pointer-events-none"
          style={{ transition: 'opacity 0.2s ease' }}
        />
      )}
    </>
  )
} 